import React, { Children } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Section } from '../components/ui/Section';
import { Button } from '../components/ui/Button';
import { PillarTile } from '../components/PillarTile';
import { ProgramCard } from '../components/ProgramCard';
import { GraduationCap, Users, Wrench, Shield, ArrowRight, ExternalLink } from 'lucide-react';
export const HomePage = () => {
  return <div className="min-h-screen pt-20" data-id="element-416">
      {/* HERO SECTION */}
      <section className="bg-[#F2F0E7] py-16 md:py-24 overflow-hidden" data-id="element-417">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-418">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-419">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-420">
              <h1 className="text-5xl md:text-7xl font-extrabold text-[#1C274C] mb-6 tracking-tight leading-tight" data-id="element-421">
                AI For Kids <span className="text-[#3AB5E8]" data-id="element-422">Africa</span>
              </h1>
              <h2 className="text-2xl md:text-3xl font-bold text-gray-700 mb-6" data-id="element-423">
                Connecting Africa's Next Generation to AI Solutions
              </h2>
              <p className="text-lg text-gray-600 mb-8 max-w-lg leading-relaxed" data-id="element-424">
                We empower African children to understand, create, and grow with
                Artificial Intelligence, safely, ethically, and creatively.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-425">
                <a href="https://forms.gle/yiZQndnfYhzdQtX68" target="_blank" rel="noopener noreferrer" data-id="element-426">
                  <Button size="lg" className="w-full sm:w-auto font-bold text-lg px-8 py-4 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all" rightIcon={<ExternalLink className="w-5 h-5" data-id="element-428" />} data-id="element-427">
                    Join the Waitlist
                  </Button>
                </a>
                <Link to="/contact" data-id="element-429">
                  <Button variant="secondary" size="lg" className="w-full sm:w-auto font-bold text-lg px-8 py-4 shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all" data-id="element-430">
                    Get Involved
                  </Button>
                </Link>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="relative" data-id="element-431">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#3AB5E8]/20 to-[#FC963E]/20 rounded-full opacity-50 blur-3xl animate-pulse" data-id="element-432"></div>
              <img src="https://cdn.magicpatterns.com/uploads/x1YDvGzeVU8YQXVtyCccxo/f49a8e45-5d48-4cb5-9cfc-cafe1ddecaa2-removebg-preview.png" alt="AI For Kids Africa Logo" className="relative rounded-3xl w-full object-contain aspect-square p-8 bg-white/80 backdrop-blur-sm border-4 border-white transform hover:scale-105 transition-transform duration-500" data-id="element-433" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* VISION & MISSION */}
      <Section className="bg-white" data-id="element-434">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-5xl mx-auto" data-id="element-435">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-[#F2F0E7] p-8 rounded-2xl border-2 border-[#3AB5E8]/20" data-id="element-436">
            <h3 className="text-2xl font-extrabold text-[#1C274C] mb-4" data-id="element-437">
              Our Vision
            </h3>
            <p className="text-gray-700 leading-relaxed" data-id="element-438">
              A future where African children are informed, ethical, and
              confident creators of artificial intelligence solutions, not just
              consumers of technology.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="bg-[#F2F0E7] p-8 rounded-2xl border-2 border-[#FC963E]/20" data-id="element-439">
            <h3 className="text-2xl font-extrabold text-[#1C274C] mb-4" data-id="element-440">
              Our Mission
            </h3>
            <p className="text-gray-700 leading-relaxed" data-id="element-441">
              AI For Kids Africa exists to connect Africa's next generation to
              accessible, ethical, and context-relevant AI solutions by
              empowering children, educators, and communities with the
              knowledge, tools, and partnerships needed to learn, create, and
              grow with artificial intelligence.
            </p>
          </motion.div>
        </div>
      </Section>

      {/* PROBLEM STATEMENT */}
      <Section className="bg-white" data-id="element-442">
        <div className="max-w-4xl mx-auto text-center bg-[#F2F0E7] p-12 rounded-3xl border-2 border-gray-200" data-id="element-443">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-8 text-[#1C274C]" data-id="element-444">
            The Problem We Are Solving
          </h2>
          <p className="text-lg text-[#1C274C] leading-relaxed mb-6 font-medium" data-id="element-445">
            Across Africa, children are increasingly exposed to digital
            technology but remain largely excluded from meaningful participation
            in the understanding, creation, and ethical use of artificial
            intelligence.
          </p>
          <p className="text-lg text-[#1C274C] leading-relaxed font-medium" data-id="element-446">
            Limited access to AI education, lack of teacher support, poor
            contextual relevance, and weak safeguards risk turning African
            children into passive consumers rather than empowered contributors
            to the AI-driven world.
          </p>
        </div>
      </Section>

      {/* CORE PILLARS */}
      <Section className="bg-[#F2F0E7]" data-id="element-447">
        <div className="text-center mb-12" data-id="element-448">
          <h2 className="text-4xl font-extrabold text-[#1C274C] mb-4" data-id="element-449">
            Our Core Pillars
          </h2>
          <p className="text-gray-600 max-w-3xl mx-auto" data-id="element-450">
            Our work is guided by four core pillars that ensure children are
            empowered, protected, and prepared to thrive in an AI-driven world.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-451">
          <PillarTile title="AI Literacy & Education" description="We help children move beyond passive use of technology by introducing them to AI in ways that are simple, engaging, and age-appropriate." icon={GraduationCap} bgColor="bg-[#3AB5E8]" ctaText="Learn More" ctaLink="/programs/students" data-id="element-452" />
          <PillarTile title="Empowering Educators & Parents" description="Children learn best when the adults around them are informed and confident. We equip teachers and parents with the knowledge and tools needed." icon={Users} bgColor="bg-[#FC963E]" ctaText="Explore Training" ctaLink="/programs/teachers" data-id="element-453" />
          <PillarTile title="Curated AI Tools & Solutions" description="Not all AI tools are suitable for children. We carefully review and highlight tools that are safe, ethical, and relevant to African learning environments." icon={Wrench} bgColor="bg-[#389C70]" ctaText="Browse Tools" ctaLink="/blog" data-id="element-454" />
          <PillarTile title="Ethical, Safe & Inclusive AI" description="Ethics and safety are not optional. We place child protection, fairness, and inclusion at the center of how AI is introduced and used." icon={Shield} bgColor="bg-[#F76874]" ctaText="Our Approach" ctaLink="/about" data-id="element-455" />
        </div>
      </Section>

      {/* PROGRAMS */}
      <Section id="programs" className="bg-white" data-id="element-456">
        <div className="text-center mb-16" data-id="element-457">
          <h2 className="text-4xl font-extrabold text-[#1C274C] mb-4" data-id="element-458">
            Our Programs
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto" data-id="element-459">
            From classrooms to communities, we work with children, teachers, and
            parents to ensure Africa's future is shaped with AI, not left behind
            by it.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto" data-id="element-460">
          <ProgramCard title="Project AI for Kids" who="Senior Primary & Secondary Students" what="Fun, age-appropriate introduction to AI through stories, play, creativity, and ethical awareness. Children learn to think critically and imagine AI solutions for real problems." image="https://cdn.magicpatterns.com/uploads/erz1J4AcmpKLgW9dAf4tmK/WhatsApp_Image_2025-12-31_at_02.05.13.jpg" link="/programs/students" ctaText="Learn More" color="blue" data-id="element-461" />
          <ProgramCard title="Project AI for Educators" who="Teachers & School Administrators" what="Capacity-building initiative helping educators confidently understand and integrate AI into education, responsibly and ethically. Includes seminars, resources, and ongoing support." image="https://cdn.magicpatterns.com/uploads/gVFcEKzfsBcFwg4mMR7R7E/IMG-20250714-WA0047.jpg" link="/programs/teachers" ctaText="Join Training" color="orange" data-id="element-462" />
        </div>
      </Section>

      {/* VALUES */}
      <Section className="bg-[#F2F0E7]" data-id="element-463">
        <div className="max-w-4xl mx-auto" data-id="element-464">
          <h2 className="text-3xl font-extrabold text-[#1C274C] mb-8 text-center" data-id="element-465">
            Our Values & Principles
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-466">
            {[{
            title: 'Child-First Design',
            desc: 'Children are at the center of everything we do'
          }, {
            title: 'Empowerment, Not Replacement',
            desc: 'We empower educators and parents, never replace them'
          }, {
            title: 'Ethical & Responsible AI',
            desc: 'Safety, fairness, and inclusion are non-negotiable'
          }, {
            title: 'Education Over Noise',
            desc: 'We build understanding and long-term impact, not hype'
          }].map((value, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: index * 0.1
          }} className="bg-white p-6 rounded-xl border-2 border-gray-100" data-id="element-467">
                <h3 className="text-xl font-bold text-[#1C274C] mb-2" data-id="element-468">
                  {value.title}
                </h3>
                <p className="text-gray-600" data-id="element-469">{value.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </Section>

      {/* CTA SECTION */}
      <section className="bg-[#FC963E] py-20" data-id="element-470">
        <div className="max-w-4xl mx-auto px-4 text-center text-white" data-id="element-471">
          <h2 className="text-3xl md:text-5xl font-extrabold mb-6" data-id="element-472">
            Do you want us in your school?
          </h2>
          <p className="text-xl text-white/90 mb-10 font-medium" data-id="element-473">
            Join our waitlist and be part of Africa's AI education revolution.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-474">
            <a href="https://forms.gle/yiZQndnfYhzdQtX68" target="_blank" rel="noopener noreferrer" data-id="element-475">
              <button className="bg-white text-[#FC963E] font-extrabold text-lg px-8 py-4 rounded-full shadow-lg hover:bg-gray-50 hover:scale-105 transition-all duration-200 inline-flex items-center gap-2" data-id="element-476">
                Join the Waitlist <ExternalLink className="w-5 h-5" data-id="element-477" />
              </button>
            </a>
            <Link to="/contact" data-id="element-478">
              <button className="bg-transparent border-2 border-white text-white font-extrabold text-lg px-8 py-4 rounded-full hover:bg-white/10 transition-all duration-200" data-id="element-479">
                Partner With Us
              </button>
            </Link>
          </div>
          <p className="mt-8 text-white/80 font-medium" data-id="element-480">
            Questions? Email us at{' '}
            <a href="mailto:info@aiforkidsafrica.com" className="underline hover:text-white" data-id="element-481">
              info@aiforkidsafrica.com
            </a>
          </p>
        </div>
      </section>
    </div>;
};